import { Box } from '@chakra-ui/react';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseDate, dateDiff, getFormatDateFromStoreDate } from 'shared-components';
// import { isAgentOrAgencyRole } from '@/utilities/roles/permissions'; // not currently used
import { ImportSource } from 'api-paths-cas';
import { ApplicationState } from '../../../store';
import { getNavigationUrls } from '../../../store/config/actions';
import { Status, VisaStatus } from '../../../store/student/core/types';
import { IAgent, IEnrolmentStatus, MaturityStatus } from '../../../types';
import { formatCurrency, formatWithDecimal, formatYears, getVisaStatusFormat } from '../../../utilities/formatting';
import { getFrom } from '../../../utilities/helpers';
import { ArrivalStatus } from '../../atoms/arrivalStatus';
import FundsMatured from '../../atoms/FundsMatured';
import { SuccessPredictor } from '../../atoms/success-predictor';
import { SidebarGroup } from '../studentActions/Group';
import { SidebarItem } from './SidebarItem';

interface Props {
  lockAgent?: boolean;
  courseName?: string;
  email?: string;
  lastTouch?: number;
  showLastTouch: boolean;
  showAgent: boolean;
  t4VisaTime?: number;
  agentNotListed?: boolean;
  importAgent: string;
  assignedAgents?: IAgent[];
  phone?: string;
  fundsMatured?: MaturityStatus[];
  courseFeeDeposit?: number;
  expectedDepositDate?: string;
  nationality?: string;
  university: string;
  visaStatus?: VisaStatus;
  currentStage: Status;
  pathway?: string;
  showArrivalStatus: boolean;
  arrivalStatus: IEnrolmentStatus['travelStatusSection'];
  visaSupport?: boolean;
  lastAcceptanceDate: string | undefined;
  isConfidenceScoreView: boolean;
  successPercentage?: number;
  onEditAgentAccess: () => void;
  onEditMaturedDate: () => void;
  onEditVisaStatus: () => void;
  onEditArrivalStatus: () => void;
  applicantId?: string;
}

const NYE = 'Not Yet Entered';

// Commented out - not currently used in component
// function getApplyUrl(
//   hasApplyAccess: boolean,
//   navigationUrls: any,
//   applicantId: string | undefined,
//   applicationId: string | undefined,
//   offerId: string | undefined,
//   isAgent: boolean,
//   importData: any,
//   university: string
// ): string {
//   let applyUrl = '';
//   if (hasApplyAccess && navigationUrls?.apply && applicantId && applicationId && offerId) {
//     if (isAgent && importData.withAgent) {
//       // && importData.agencyId === auth.agencyId
//       applyUrl = `${navigationUrls.apply}/application/${university}/${applicantId}/offers/${offerId}`;
//     } else {
//       applyUrl = `${navigationUrls.apply}/application/${university}/${applicantId}/${applicationId}/offers/${offerId}`;
//     }
//   }
//   return applyUrl;
// }

function StudentSidebar(props: Props) {
  const {
    lockAgent,
    courseName = NYE,
    email = NYE,
    lastTouch = 0,
    showLastTouch,
    showArrivalStatus,
    arrivalStatus,
    showAgent,
    t4VisaTime = 0,
    importAgent,
    assignedAgents = [],
    phone = NYE,
    fundsMatured = [{ type: 'INFO', value: 'N/A' }],
    courseFeeDeposit,
    expectedDepositDate,
    nationality = NYE,
    visaStatus = VisaStatus.NONE,
    currentStage = Status.PRE_DEPOSIT,
    university,
    isConfidenceScoreView,
    successPercentage,
    onEditAgentAccess,
    onEditVisaStatus,
    onEditArrivalStatus,
    pathway,
    visaSupport,
    lastAcceptanceDate,
    // applicantId - not currently used
  } = props;

  const dispatch = useDispatch();
  // const role = useSelector((state: ApplicationState) => state.auth.role); // not currently used
  // const isAgent = isAgentOrAgencyRole(role); // not currently used
  // const navigationUrls = useSelector((state: ApplicationState) => state.config.navigationUrls); // not currently used
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const hasApplyAccess = importData?.source === ImportSource.APPLY || !!importData?.applicantId;
  // const applicationId = importData?.offers[0]?.applicationId; // not currently used
  // const offerId = importData?.offers[0]?.offerId; // not currently used

  useEffect(() => {
    if (university && hasApplyAccess) {
      dispatch(getNavigationUrls(university));
    }
  }, [university, hasApplyAccess, dispatch]);

  // Commented out - not currently used in component
  // const applyUrl = getApplyUrl(
  //   hasApplyAccess,
  //   navigationUrls,
  //   applicantId,
  //   applicationId,
  //   offerId,
  //   isAgent,
  //   importData,
  //   university
  // );

  const visaSupportAgent: IAgent = {
    type: 'student',
    id: 'EnrolyVIS',
    agentName: 'EnrolyVIS',
    displayName: 'Visa Support Opt In',
  };

  // Visa support shows as an 'agent'.
  const getDisplayAgents = () => {
    const displayAgents = [...assignedAgents];
    if (visaSupport) {
      displayAgents.unshift(visaSupportAgent);
    }
    return displayAgents;
  };
  const displayAgents = getDisplayAgents();

  const isAgentRemoved =
    displayAgents.length === 1 && displayAgents[0].type === 'student' && displayAgents[0].displayName === '<Removed>';

  const getAgentStatus = () => {
    if (displayAgents.length === 0 && importAgent === '') {
      return;
    } else if (displayAgents.length === 0 && importAgent !== '') {
      return 'warning';
    } else if (displayAgents.length === 1 && displayAgents[0].type === 'student') {
      if (displayAgents[0].agentName) {
        return;
      } else if (displayAgents[0].id === 'EnrolyVIS') {
        return;
      } else if (isAgentRemoved) {
        return 'muted';
      }
      return;
    }
    return;
  };

  const getAgentValue = (): string => {
    if (displayAgents.length === 0) {
      return getEmptyAgentValue();
    }

    const agents = [...displayAgents];
    // prioritize last added agent
    agents.reverse();
    let displayAgent;
    displayAgent = agents.find(a => a.type === 'master');
    if (!displayAgent) displayAgent = agents.find(a => a.type === 'sub');
    if (!displayAgent) displayAgent = agents.find(a => a.type === 'other');
    if (!displayAgent) displayAgent = agents.find(a => a.type === 'student');
    if (!displayAgent) return '';

    const displayNum = agents.length - 1;

    let str = displayAgent.displayName || '';

    if (displayNum > 0) str += ` +${displayNum}`;

    return str;
  };

  const getEmptyAgentValue = (): string => {
    let str;
    if (importAgent !== '') {
      str = importAgent;
    } else {
      str = 'None';
    }
    return str;
  };

  const agentValueElement = () => {
    const getAgentTooltip = () => {
      if (displayAgents.length > 0 && !isAgentRemoved) {
        displayAgents.map((agent, i) => {
          return <div key={i}>{agent.displayName}</div>;
        });
      }

      if (displayAgents.length === 0 && importAgent !== '') {
        return 'Unmapped agent';
      }

      return getEmptyAgentValue();
    };

    const value = (
      <>
        {getAgentValue()}

        {lockAgent ? (
          <Box mr={-1} as="span">
            {' '}
            <FontAwesomeIcon icon={faLock} size="sm" className="utility-icon" />
          </Box>
        ) : null}
      </>
    );

    return (
      <SidebarItem
        data-selector="studentSummary-agent"
        label="Agent"
        value={value}
        valueColor={getAgentStatus()}
        tooltip={getAgentTooltip()}
        onClickEdit={onEditAgentAccess}
      />
    );
  };

  const hasT4Visa = true; // CS-356 (always show T4 visa time) t4VisaTime && t4VisaTime > 0;

  const getT4VisaStatus = () => {
    if (hasT4Visa) {
      if (t4VisaTime > 730) {
        return {
          isBadge: true,
          badgeProps: { colorScheme: 'red' },
        };
      }
      return {
        isBadge: true,
        badgeProps: { colorScheme: 'green' },
      };
    }
  };

  const getLastTouchStatus = (time: number = lastTouch) => {
    if (time === 0) {
      return undefined;
    }
    const nowDate = parseDate(new Date()); //todays date
    const endDate = parseDate(new Date(time)); // convert timestamp to date

    if (!nowDate || !endDate) {
      return undefined;
    }

    const days = dateDiff(endDate, nowDate, 'days');
    if (days < 3) {
      return {
        isBadge: true,
        badgeProps: { colorScheme: 'green' },
      };
    } else if (days < 12) {
      return {
        isBadge: true,
        badgeProps: { colorScheme: 'orange' },
      };
    }
    return {
      isBadge: true,
      badgeProps: { colorScheme: 'red' },
    };
  };

  const formatVisaStatus = (value: string) => {
    if (value === 'None') {
      return { value };
    }
    const visaStatus = getVisaStatusFormat(value);
    return {
      tooltip: visaStatus.onHoverText,
      isBadge: true,
      badgeProps: { colorScheme: visaStatus.colorScheme },
    };
  };

  const hasVisaStatus =
    currentStage === Status.CAS_ISSUED ||
    currentStage === Status.VISA_ISSUED ||
    currentStage === Status.VISA_REFUSED ||
    currentStage === Status.DEFERRED ||
    currentStage === Status.CLOSED_LOST;

  const getDepositValue = () => {
    if (!courseFeeDeposit && expectedDepositDate) {
      return getFormatDateFromStoreDate(expectedDepositDate);
    }
    return `£${formatCurrency(courseFeeDeposit ?? 0)}`;
  };

  return (
    <SidebarGroup header="Summary">
      <SidebarItem label="Nationality" value={nationality} data-selector="studentSummary-nationality" />

      <SidebarItem label="Email address" value={email} data-selector="studentSummary-email" />

      <SidebarItem
        label="Phone number"
        value={phone === NYE ? phone : `+${phone}`}
        data-selector="studentSummary-phone"
      />

      <SidebarItem label="Course" value={courseName} data-selector="studentSummary-courseName" />

      {showAgent && agentValueElement()}

      <SidebarItem
        label="Institution"
        value={pathway ? `${pathway} - ${university}` : university}
        data-selector="studentSummary-universityName"
      />

      {isConfidenceScoreView && (
        <SidebarItem
          label="Confidence"
          value={<SuccessPredictor successPercentage={successPercentage} />}
          valueBoxProps={{ py: 1 }}
          data-selector="successPredictor"
        />
      )}

      <SidebarItem label="Deposit" value={getDepositValue()} data-selector="studentSummary-deposit" />

      <SidebarItem
        label="Funds mature"
        value={<FundsMatured maturityStatus={fundsMatured} />}
        data-selector="studentSummary-fundsMatured"
      />

      {showArrivalStatus && (
        <ArrivalStatus
          arrivalStatus={arrivalStatus}
          lastAcceptanceDate={lastAcceptanceDate}
          onEdit={onEditArrivalStatus}
          data-selector="studentSummary-arrivalStatus"
        />
      )}

      {hasVisaStatus && (
        <SidebarItem
          label="Visa status"
          value={visaStatus}
          {...formatVisaStatus(visaStatus)}
          onClickEdit={onEditVisaStatus}
          data-selector="studentSummary-visaStatus"
        />
      )}

      {hasT4Visa && (
        <SidebarItem
          label="Tier 4/SR time"
          value={`${formatWithDecimal(t4VisaTime)}d (${formatYears(t4VisaTime)})`}
          {...getT4VisaStatus()}
          data-selector="studentSummary-tier4SRTime"
        />
      )}

      {showLastTouch && (
        <SidebarItem
          label="Last touch"
          value={lastTouch === 0 ? 'Never' : getFrom(lastTouch)}
          {...getLastTouchStatus(lastTouch)}
          data-selector="studentSummary-lastTouchDate"
        />
      )}
    </SidebarGroup>
  );
}

export default StudentSidebar;
